import type { paths } from "@/api-schema";
import { alphartisApiAxios } from "@/config/axios";
import { useQuery } from "@tanstack/react-query";
import { handlePaginatedParameters } from "../_helpers/handle-parameters";
import { optionsWithDefaultsPaginated } from "../_helpers/options-with-defaults";
import { PaginatedOptions, UseQueryOptionsNoKeyAndFn } from "../_types";

export const DEPARTMENT_SEARCH_RESOURCE = "department" as const;
export const DEPARTMENT_SEARCH_OPERATION = "search" as const;
export const DEPARTMENT_SEARCH_API_PATH = `/v1/${DEPARTMENT_SEARCH_RESOURCE}/${DEPARTMENT_SEARCH_OPERATION}` as const;

export type DepartmentSearchParameters = paths[typeof DEPARTMENT_SEARCH_API_PATH]["post"]["parameters"]["query"];
export type DepartmentSearchBody =
  paths[typeof DEPARTMENT_SEARCH_API_PATH]["post"]["requestBody"]["content"]["application/json"];
export type DepartmentSearchResponse =
  paths[typeof DEPARTMENT_SEARCH_API_PATH]["post"]["responses"][200]["content"]["application/json"];

export function useDepartmentSearchQuery({
  options,
  enabled,
  queryOptions,
}: {
  options?: PaginatedOptions<DepartmentSearchParameters, DepartmentSearchBody>;
  enabled?: boolean;
  queryOptions?: UseQueryOptionsNoKeyAndFn<
    DepartmentSearchResponse,
    Error,
    DepartmentSearchResponse,
    readonly unknown[]
  >;
} = {}) {
  const optionsWithDefaults =
    optionsWithDefaultsPaginated<PaginatedOptions<DepartmentSearchParameters, DepartmentSearchBody>>(options);

  const requestParameters = {
    ...(optionsWithDefaults.parameters || {}),
    ...handlePaginatedParameters(optionsWithDefaults.parameters),
  };

  return useQuery({
    queryKey: [
      DEPARTMENT_SEARCH_RESOURCE,
      DEPARTMENT_SEARCH_OPERATION,
      { parameters: requestParameters, body: optionsWithDefaults.body },
    ],
    queryFn: async () => {
      const response = await alphartisApiAxios.post<DepartmentSearchResponse>(
        DEPARTMENT_SEARCH_API_PATH,
        optionsWithDefaults.body,
        {
          params: requestParameters,
        },
      );

      return response.data;
    },
    ...queryOptions,
    enabled,
  });
}
